'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useMarketConfig } from '../../provider/MarketConfigProvider.js';
import { PersistenceRestoredContext } from '../../provider/PersistenceRestoredContext.js';
import { sdkEvents } from '../events.js';
import { createListHook } from './createListHook.js';
export function createMarketChartHook(options) {
  const useBase = createListHook(options);
  return function useMarketChart(args, opts) {
    const client = useQueryClient();
    const needsConfig = args.property === 'market_cap' && (args.mints?.length ?? 0) === 0;
    const enabled = opts?.enabled !== false;
    const active = enabled && needsConfig;
    const store = useMemo(() => marketChartContextStore(client, args.pubkey, args.outcomeIndex, active), [client, args.pubkey, args.outcomeIndex, active]);
    const context = useSyncExternalStore(store.subscribe, store.read, store.read);
    const config = useMarketConfig(context.address);
    const quote = context.virtualQuoteD6 ?? config?.initialVirtualQuote;
    const invalidQuote = quote !== undefined && (!/^[1-9]\d*$/.test(quote) || quote.length > 20 || quote.length === 20 && quote > '18446744073709551615');
    const virtualQuoteD6 = invalidQuote ? undefined : quote;
    const restored = useContext(PersistenceRestoredContext);
    useEffect(() => {
      if (restored && invalidQuote) sdkEvents.emit('cache:diagnostic', {
        kind: 'market-chart',
        reason: 'invalid-virtual-quote',
        specName: options.spec.specName
      });
    }, [invalidQuote, restored]);
    return useBase({
      ...args,
      virtualQuoteD6
    }, {
      ...opts,
      enabled: enabled && (!needsConfig || virtualQuoteD6 !== undefined)
    });
  };
}
function marketChartContextStore(client, pubkey, outcomeIndex, needed) {
  let previous;
  let outcomeMint;
  const read = () => {
    const market = needed ? client.getQueryData(['markets', 'entity', pubkey]) : undefined;
    const timed = market?.resolutionMode === 'timed';
    const address = timed ? undefined : market?.marketConfig;
    outcomeMint = timed ? market.outcomes?.find(outcome => outcome.outcomeIndex === Number(outcomeIndex ?? '0'))?.mint : undefined;
    const virtualQuoteD6 = readTimedVirtualQuoteD6(client, outcomeMint);
    if (previous === undefined || previous.address !== address || previous.virtualQuoteD6 !== virtualQuoteD6) previous = {
      address,
      virtualQuoteD6
    };
    return previous;
  };
  const subscribe = notify => {
    if (!needed) return () => {};
    return client.getQueryCache().subscribe(({
      query
    }) => {
      const key = query.queryKey;
      if (key[0] === 'markets' && key[1] === 'entity' && key[2] === pubkey || outcomeMint !== undefined && key[0] === 'outcomes' && key[1] === 'entity' && key[2] === outcomeMint) notify();
    });
  };
  return {
    read,
    subscribe
  };
}
function readTimedVirtualQuoteD6(client, mint) {
  if (mint === undefined) return undefined;
  const outcome = client.getQueryData(['outcomes', 'entity', mint]);
  const pool = outcome?.currentPoolState;
  return pool !== undefined && 'virtualQuoteReserveD6' in pool ? pool.virtualQuoteReserveD6 : outcome?.virtualQuoteD6;
}